import React, { useState } from 'react';
import authService from '../services/authService';
import { showSuccess, showError } from '../utils/toast';
import './MyProfile.css';

/**
 * TD-followup-edicao-perfil-usuario (Sessão 23) — Modal "Meu Perfil".
 *
 * Permite ao usuário comum (não-admin) editar o próprio nome e trocar
 * a própria senha. Antes desta tela, operadores dependiam de admin pra
 * qualquer mudança no perfil — quebra de menor privilégio + UX ruim.
 *
 * Props:
 *   isOpen     — controla visibilidade
 *   onClose    — fecha modal
 *   user       — objeto user atual (id, email, nome, role)
 *   onUpdated  — callback chamado com user atualizado após save de nome
 *
 * Layout: 2 forms independentes (nome + senha), cada uma com seu próprio
 * save. UX clara: usuário não precisa preencher tudo pra salvar parte.
 */
function MyProfile({ isOpen, onClose, user, onUpdated }) {
  const [nome, setNome] = useState(user?.nome || '');
  const [savingNome, setSavingNome] = useState(false);

  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmaSenha, setConfirmaSenha] = useState('');
  const [savingSenha, setSavingSenha] = useState(false);

  if (!isOpen) return null;

  const handleSalvarNome = async (e) => {
    e.preventDefault();
    const trimmed = (nome || '').trim();
    if (!trimmed) {
      showError('Nome não pode ficar vazio.');
      return;
    }
    if (trimmed === user?.nome) {
      showError('Nome não mudou.');
      return;
    }
    setSavingNome(true);
    try {
      const updated = await authService.updateMe(trimmed);
      showSuccess('Nome atualizado!');
      if (onUpdated) onUpdated(updated);
    } catch (err) {
      showError(err.message || 'Erro ao atualizar nome.');
    } finally {
      setSavingNome(false);
    }
  };

  const handleTrocarSenha = async (e) => {
    e.preventDefault();
    if (!senhaAtual) {
      showError('Informe sua senha atual.');
      return;
    }
    if (novaSenha.length < 8) {
      showError('Nova senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    if (novaSenha !== confirmaSenha) {
      showError('Confirmação não bate com a nova senha.');
      return;
    }
    if (novaSenha === senhaAtual) {
      showError('Nova senha deve ser diferente da atual.');
      return;
    }
    setSavingSenha(true);
    try {
      await authService.changePassword(senhaAtual, novaSenha);
      showSuccess('Senha trocada com sucesso!');
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmaSenha('');
    } catch (err) {
      // Mensagem específica do backend (ex: "Senha atual incorreta")
      showError(err.message || 'Erro ao trocar senha.');
    } finally {
      setSavingSenha(false);
    }
  };

  return (
    <div
      className="myprofile-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="myprofile-title"
      onClick={savingNome || savingSenha ? undefined : onClose}
    >
      <div
        className="myprofile-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="myprofile-header">
          <h3 id="myprofile-title">
            <span className="material-symbols-outlined" style={{ fontSize: '20px', verticalAlign: 'text-bottom' }}>person</span>
            {' Meu Perfil'}
          </h3>
          <button
            type="button"
            className="myprofile-close"
            onClick={onClose}
            disabled={savingNome || savingSenha}
            aria-label="Fechar"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="myprofile-readonly">
          <div><strong>Email:</strong> {user?.email}</div>
          <div><strong>Função:</strong> {(user?.role || '').toUpperCase()}</div>
        </div>

        {/* Form 1 — atualizar nome */}
        <form className="myprofile-section" onSubmit={handleSalvarNome}>
          <h4>Nome</h4>
          <label htmlFor="myprofile-nome" className="sr-only">Nome</label>
          <input
            id="myprofile-nome"
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            disabled={savingNome}
            placeholder="Seu nome completo"
            maxLength={120}
          />
          <button
            type="submit"
            className="myprofile-btn-primary"
            disabled={savingNome}
          >
            {savingNome ? 'Salvando...' : 'Atualizar nome'}
          </button>
        </form>

        {/* Form 2 — trocar senha */}
        <form className="myprofile-section" onSubmit={handleTrocarSenha}>
          <h4>Trocar senha</h4>
          <label htmlFor="myprofile-senha-atual">Senha atual</label>
          <input
            id="myprofile-senha-atual"
            type="password"
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
            disabled={savingSenha}
            autoComplete="current-password"
          />
          <label htmlFor="myprofile-nova-senha">Nova senha (mín. 8 caracteres)</label>
          <input
            id="myprofile-nova-senha"
            type="password"
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            disabled={savingSenha}
            autoComplete="new-password"
          />
          <label htmlFor="myprofile-confirma-senha">Confirmar nova senha</label>
          <input
            id="myprofile-confirma-senha"
            type="password"
            value={confirmaSenha}
            onChange={(e) => setConfirmaSenha(e.target.value)}
            disabled={savingSenha}
            autoComplete="new-password"
          />
          <button
            type="submit"
            className="myprofile-btn-primary"
            disabled={savingSenha}
          >
            {savingSenha ? 'Trocando...' : 'Trocar senha'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default MyProfile;
